<template>
  <div style="display: inline-block;">
    <el-button
        :type="buttonType"
        plain
        :icon="buttonIcon"
        size="small"
        @click="openDialog"
    >{{ buttonText }}</el-button>
    <el-dialog :visible.sync="dialogVisible" width="540px" title="选择目标目录" :close-on-click-modal="false">
      <div>
        <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
          <span style="font-size: 14px;">已勾选：<b>{{ selectList.length }}</b> 条模型配置</span>
          <el-tag :type="protocolFilter === 'anthropic' ? 'success' : 'warning'" size="mini" effect="plain">
            仅导出 {{ protocolLabel }} 协议
          </el-tag>
        </div>
        <div style="display: flex; align-items: center;">
          <el-input v-model="selectedPath" placeholder="请选择目标目录" readonly>
            <i slot="prefix" class="el-icon-folder-opened" style="line-height: 36px; color: #909399;"></i>
          </el-input>
          <el-button type="primary" @click="selectDirectory" style="margin-left: 10px; flex-shrink: 0;">浏览</el-button>
        </div>
        <div v-if="selectedPath" style="margin-top: 8px; font-size: 13px; color: #67C23A;">
          <i class="el-icon-success"></i> 已选择：{{ selectedPath }}
        </div>
      </div>
      <span slot="footer">
        <el-button @click="dialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" size="small" :loading="exportLoading" :disabled="!selectedPath" @click="confirmExport">
          确认导出
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 模型配置导出组件 — 将勾选的模型配置导出为 Claude Code / OpenCode 配置文件
 * 点击按钮 → 校验协议类型 → 弹窗选择目录 → 确认后写入文件
 *
 * @author PeterLee
 * @since 2026-05-23
 */
export default {
  name: "ModelConfigExportPlus",
  props: {
    // 按钮文字
    buttonText: { type: String, default: '导出' },
    // 按钮类型（primary / success / warning / info / danger）
    buttonType: { type: String, default: 'primary' },
    // 按钮图标
    buttonIcon: { type: String, default: 'el-icon-upload' },
    // 勾选的模型列表
    selectList: { type: Array, default: () => [] },
    // 导出类型：claude-code | opencode
    exportType: { type: String, required: true, validator: v => ['claude-code', 'opencode'].includes(v) },
  },
  data() {
    return {
      dialogVisible: false,
      selectedPath: '',
      directoryHandle: null,
      exportLoading: false,
    }
  },
  computed: {
    /** 当前导出类型允许的协议 */
    protocolFilter() {
      return this.exportType === 'claude-code' ? 'anthropic' : 'openai';
    },
    /** 协议中文名 */
    protocolLabel() {
      return this.exportType === 'claude-code' ? 'Anthropic' : 'OpenAI';
    },
  },
  methods: {
    /** 打开弹窗前先校验勾选数据的协议类型 */
    async openDialog() {
      if (this.selectList.length === 0) {
        this.$message.warning('请先勾选要导出的模型配置');
        return;
      }
      const invalid = this.selectList.filter(item => item.protocolType !== this.protocolFilter);
      if (invalid.length > 0) {
        const names = invalid.map(i => `【${i.providerName}】${i.modelName}`).join('、');
        this.$message.error(`以下配置不是 ${this.protocolLabel} 协议，无法导出：${names}`);
        return;
      }
      this.selectedPath = '';
      this.directoryHandle = null;
      this.dialogVisible = true;
    },
    /** 选择目录 */
    async selectDirectory() {
      try {
        const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
        this.directoryHandle = handle;
        this.selectedPath = handle.name;
      } catch {
        // 用户取消选择
      }
    },
    /** 确认导出 */
    async confirmExport() {
      if (!this.directoryHandle) return;
      this.exportLoading = true;
      try {
        if (this.exportType === 'claude-code') {
          await this.doExportClaudeCode();
        } else {
          await this.doExportOpenCode();
        }
        this.$message.success(`${this.protocolLabel} 配置已生成`);
        this.dialogVisible = false;
      } catch (e) {
        console.error('导出失败', e);
        this.$message.error('配置生成失败');
      } finally {
        this.exportLoading = false;
      }
    },
    /** 写入文件到指定目录的子目录下 */
    async writeFileToDir(rootHandle, subDir, fileName, content) {
      let dirHandle = rootHandle;
      if (subDir) {
        const parts = subDir.replace(/\\/g, '/').split('/').filter(Boolean);
        for (const part of parts) {
          dirHandle = await dirHandle.getDirectoryHandle(part, { create: true });
        }
      }
      const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(content);
      await writable.close();
    },
    /** 生成 Claude Code 配置 */
    async doExportClaudeCode() {
      const primary = this.selectList[0];
      const env = {
        ANTHROPIC_AUTH_TOKEN: primary.apiKey,
        ANTHROPIC_BASE_URL: primary.baseUrl,
        ANTHROPIC_MAX_TOKENS: "48000"
      };
      // 按勾选顺序将 modelName 分配到不同模型槽位
      const slots = [
        'ANTHROPIC_MODEL',
        'ANTHROPIC_DEFAULT_OPUS_MODEL',
        'ANTHROPIC_DEFAULT_SONNET_MODEL',
        'ANTHROPIC_DEFAULT_HAIKU_MODEL',
        'CLAUDE_CODE_SUBAGENT_MODEL'
      ];
      this.selectList.forEach((item, i) => {
        if (i < slots.length) {
          env[slots[i]] = item.modelName;
        }
      });
      await this.writeFileToDir(this.directoryHandle, '.claude', 'settings.json', JSON.stringify({
        env,
        permissions: { allow: [], deny: [] },
        alwaysThinkingEnabled: false,
        theme: "dark"
      }, null, 2));
    },
    /** 生成 OpenCode 配置 */
    async doExportOpenCode() {
      const providers = {};
      this.selectList.forEach(item => {
        const key = item.providerName
            ? item.providerName.toLowerCase().replace(/\s+/g, '-')
            : 'provider';
        if (!providers[key]) {
          providers[key] = {
            npm: '@ai-sdk/openai-compatible',
            name: item.providerName,
            options: { baseURL: item.baseUrl, apiKey: item.apiKey },
            models: {}
          };
        }
        providers[key].models[item.modelName] = {
          name: item.modelName,
          tool_call: true,
          reasoning: true,
          input: ["text"],
          cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
          contextWindow: 200000,
          maxTokens: 8192,
          api: 'openai-responses'
        };
      });
      const firstKey = Object.keys(providers)[0];
      await this.writeFileToDir(this.directoryHandle, '.config/opencode', 'opencode.json', JSON.stringify({
        $schema: "https://opencode.ai/config.json",
        provider: providers,
        model: firstKey + '/' + this.selectList[0].modelName
      }, null, 2));
    },
  }
}
</script>

<style scoped>
</style>
